import { useApolloClient, useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { gql } from '~/__generated__'
import type { PlaylistVisibilityEnum } from '~/__generated__/graphql'
import { GET_PLAYLISTS } from '~/features/play_list/graphql/query/getPlaylistRegistration'

const CREATE_PLAYLIST = gql(`
  mutation createPlaylist(
    $title: String!
    $description: String
    $visibility: PlaylistVisibilityEnum
    $rowOrderPosition: Int
  ) {
    createPlaylist(
      input: {
        title: $title
        description: $description
        visibility: $visibility
        rowOrderPosition: $rowOrderPosition
      }
    ) {
      errors
      playlist {
        id
        title
        description
        visibility
      }
    }
  }
`)

const UPDATE_PLAYLIST = gql(`
  mutation updatePlaylist(
    $id: ID!
    $title: String
    $description: String
    $visibility: PlaylistVisibilityEnum
  ) {
    updatePlaylist(
      input: { id: $id, title: $title, description: $description, visibility: $visibility }
    ) {
      errors
      playlist {
        id
        title
        description
        visibility
      }
    }
  }
`)

const DELETE_PLAYLIST = gql(`
  mutation deletePlaylist($id: ID!) {
    deletePlaylist(input: { id: $id }) {
      errors
    }
  }
`)

export interface PlayListFormValues {
  title: string
  description?: string
  visibility?: PlaylistVisibilityEnum
}

interface CreatedPlayList {
  id: string
  title: string
}


interface PlayListActionApi {
  createPlayListApi: (values: PlayListFormValues) => Promise<CreatedPlayList>
  editPlayListApi: (id: number, values: PlayListFormValues) => Promise<void>
  deletePlayListApi: (id: number) => Promise<void>
}

export function usePlayListActionApi(): PlayListActionApi {
  const apolloClient = useApolloClient()

  const [createPlaylist] = useMutation(CREATE_PLAYLIST, {
    refetchQueries: [{ query: GET_PLAYLISTS }],
  })
  const [updatePlaylist] = useMutation(UPDATE_PLAYLIST)
  const [deletePlaylist] = useMutation(DELETE_PLAYLIST, {
    refetchQueries: [{ query: GET_PLAYLISTS }],
  })

  const revalidateApiForPageUpdate = useCallback(async () => {
    await apolloClient.refetchQueries({ include: ['getLibrary'] })
  }, [apolloClient])

  const createPlayListApi = useCallback(
    async ({ title, description, visibility }: PlayListFormValues) => {
      const { data } = await createPlaylist({
        variables: { title, description, visibility, rowOrderPosition: 0 },
      })
      const errors = data?.createPlaylist?.errors
      if (errors && errors.length > 0) {
        throw new Error(errors.join(', '))
      }
      const playlist = data?.createPlaylist?.playlist
      if (!playlist) {
        throw new Error('Failed to create playlist')
      }
      await revalidateApiForPageUpdate()
      return { id: String(playlist.id), title: playlist.title ?? title }
    },
    [createPlaylist, revalidateApiForPageUpdate],
  )

  const editPlayListApi = useCallback(
    async (id: number, { title, description, visibility }: PlayListFormValues) => {
      const { data } = await updatePlaylist({
        variables: { id: String(id), title, description, visibility },
      })
      const errors = data?.updatePlaylist?.errors
      if (errors && errors.length > 0) {
        throw new Error(errors.join(', '))
      }
      await revalidateApiForPageUpdate()
    },
    [updatePlaylist, revalidateApiForPageUpdate],
  )

  const deletePlayListApi = useCallback(
    async (id: number) => {
      const { data } = await deletePlaylist({ variables: { id: String(id) } })
      const errors = data?.deletePlaylist?.errors
      if (errors && errors.length > 0) {
        throw new Error(errors.join(', '))
      }
      await revalidateApiForPageUpdate()
    },
    [deletePlaylist, revalidateApiForPageUpdate],
  )

  return { createPlayListApi, editPlayListApi, deletePlayListApi }
}
